import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flag, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loading, EmptyState } from "@/components/common/Loading";

// Otter Assist admin — an Otter-Assist-SCOPED view over the EXISTING moderation store. There is no
// competing moderation lifecycle here: post moderation still happens in the platform queue.

export function OaReports() {
  const { data, isLoading } = useQuery({
    queryKey: ["oa-admin-reports"],
    queryFn: async () => (await api.get("/admin/otter-assist/reports")).data,
  });

  if (isLoading) return <Loading />;
  if (!data?.items?.length) {
    return <EmptyState icon={Flag} title="No Otter Assist reports"
      description="Reported Otter Assist posts appear here." testId="oa-reports-empty" />;
  }

  return (
    <div className="space-y-3" data-testid="oa-admin-reports">
      <p className="text-[12px] text-muted-foreground">
        Reports on Otter Assist posts. Acting on the post itself still happens in the platform
        Moderation queue — this view adds the Assist context and the Assist-level intervention.
      </p>
      {data.items.map((row) => (
        <Card key={row.id} className="p-4" data-testid={`oa-report-${row.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{row.status}</Badge>
                <span className="text-[12px] font-[600]">{row.reason || row.category || "Report"}</span>
                <span className="text-[11px] text-muted-foreground">
                  {row.created_at?.slice(0, 16).replace("T", " ")}
                </span>
              </div>
              {row.details ? (
                <p className="mt-1 max-w-2xl text-[12px] text-foreground/80">{row.details}</p>
              ) : null}
              <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                post {row.content_id}{row.assist ? ` · assist ${row.assist.id}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {row.assist ? (
                <Button asChild size="sm" variant="outline" className="h-7 text-[11px]">
                  <Link to={`/dashboard/admin/otter-assist/assist/${row.assist.id}`}
                    data-testid={`oa-report-inspect-${row.id}`}>Inspect Assist</Link>
                </Button>
              ) : null}
              <Button asChild size="sm" variant="outline" className="h-7 text-[11px]">
                <Link to="/dashboard/admin/moderation" data-testid={`oa-report-queue-${row.id}`}>
                  <ScrollText className="mr-1.5 h-3 w-3" /> Moderation queue
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default OaReports;
